import { Box, Text, useColorModeValue } from '@chakra-ui/react';
import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { AnalysisResult, PLAYER_COLORS } from '../types';
import { translate } from '../../../i18n';
import { escapeHtml } from '../utils/court-recording.utils';

interface Props {
  data: AnalysisResult;
}

export default function ShotChart({ data }: Props) {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const bgCard = useColorModeValue('white', 'navy.800');
  const colorMode = useColorModeValue('light', 'dark') as 'light' | 'dark';
  const gridColor = useColorModeValue('#E2E8F0', '#2D3748');

  const hasCourtCoords = data.court?.detected ?? false;
  const shotEvents = useMemo(() => data.events.filter((e) => e.type === 'shot' && e.position), [data.events]);
  const playerIds = useMemo(() => Object.keys(data.stats), [data.stats]);

  // Pre-index player frames by frame number for O(1) lookup
  const playerFrameIndex = useMemo(() => {
    const index: Record<string, Map<number, [number, number]>> = {};
    for (const pid of Object.keys(data.players)) {
      const map = new Map<number, [number, number]>();
      for (const f of data.players[pid]) {
        map.set(f.frame, f.center);
      }
      index[pid] = map;
    }
    return index;
  }, [data.players]);

  const series = useMemo(() => {
    const seriesMap: Record<string, { x: number; y: number; time: number; angle: number }[]> = {};
    playerIds.forEach((pid) => (seriesMap[pid] = []));

    for (const shot of shotEvents) {
      if (!shot.position) continue;
      let closestPid = playerIds[0];
      let closestDist = Infinity;

      for (const pid of playerIds) {
        const frameMap = playerFrameIndex[pid];
        if (!frameMap) continue;
        // Check frames within ±5 of shot frame
        for (let offset = 0; offset <= 5; offset++) {
          const center = frameMap.get(shot.frame + offset) || frameMap.get(shot.frame - offset);
          if (center) {
            const dx = center[0] - shot.position[0];
            const dy = center[1] - shot.position[1];
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < closestDist) {
              closestDist = dist;
              closestPid = pid;
            }
            break;
          }
        }
      }

      if (!seriesMap[closestPid]) seriesMap[closestPid] = [];
      const useCourtPos = hasCourtCoords && shot.position_m;
      seriesMap[closestPid].push({
        x: useCourtPos ? shot.position_m![0] : shot.position[0],
        y: useCourtPos ? shot.position_m![1] : shot.position[1],
        time: shot.time,
        angle: shot.angle_change || 0,
      });
    }

    return playerIds.map((pid, i) => ({
      name: `${translate('court_recording.player')} ${pid}`,
      data: seriesMap[pid] || [],
      color: PLAYER_COLORS[i % PLAYER_COLORS.length],
    }));
  }, [shotEvents, playerIds, playerFrameIndex, hasCourtCoords]);

  const xMax = hasCourtCoords ? (data.court?.dimensions_m?.width ?? 10) : data.video.width;
  const yMax = hasCourtCoords ? (data.court?.dimensions_m?.length ?? 20) : data.video.height;
  const unit = hasCourtCoords ? 'm' : 'px';

  const options = useMemo<ApexCharts.ApexOptions>(() => ({
    chart: {
      type: 'scatter' as const,
      toolbar: { show: false },
      zoom: { enabled: false },
      background: 'transparent',
    },
    theme: { mode: colorMode },
    xaxis: {
      title: { text: `${translate('court_recording.x_position')} (${unit})` },
      min: 0,
      max: xMax,
      tickAmount: 5,
    },
    yaxis: {
      title: { text: `${translate('court_recording.y_position')} (${unit})` },
      min: 0,
      max: yMax,
      reversed: !hasCourtCoords,
      tickAmount: 5,
    },
    markers: { size: 8 },
    tooltip: {
      custom: ({ seriesIndex, dataPointIndex, w }) => {
        const point = w.config.series[seriesIndex].data[dataPointIndex];
        const name = escapeHtml(w.config.series[seriesIndex].name);
        return `<div style="padding:8px;font-size:12px">
          <b>${name}</b><br/>
          ${translate('court_recording.time')}: ${point.time.toFixed(1)}s<br/>
          ${translate('court_recording.angle')}: ${point.angle.toFixed(0)}°
        </div>`;
      },
    },
    legend: { position: 'top' },
    grid: { borderColor: gridColor },
  }), [colorMode, gridColor, xMax, yMax, unit, hasCourtCoords]);

  return (
    <Box bg={bgCard} borderRadius="12px" p={4}>
      <Text fontSize="md" fontWeight="600" color={textColor} mb={3}>{translate('court_recording.shot_chart')}</Text>
      {shotEvents.length === 0 ? (
        <Text color="secondaryGray.600" textAlign="center" py={8}>{translate('court_recording.no_shots_detected')}</Text>
      ) : (
        <ReactApexChart options={options} series={series} type="scatter" height={320} />
      )}
    </Box>
  );
}
